// ─── ROOT APP — assembled terminal ────────────────────────────────────────────
// globalSignals state — bus between tabs and SignalBoard
// handleData callback — memoized, passed to every tab as onData prop
// Tab routing · Settings modal gate · Clock · Per-tab error boundaries

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Settings, User } from "lucide-react";

// Core
import {
  C, s, font, backendFetch, FRED_BASE, EIA_BASE, NousIcon,
  INTERNAL_TOKEN_KEY, internalTokenPayload,
} from "./core";
import { newsService } from "./NewsService";
import { liveRows } from "./placeholder";

// Config
import { SettingsModal, loadKeys } from "./config";
import { SettingsPanel } from "./SettingsPanel";
import { AcknowledgementModal } from "./AcknowledgementModal";
import { clearAcknowledged, isAcknowledged, recordAcknowledged } from "./acknowledgement";
import { AuthScreen } from "./AuthScreen";
import { PublicLedger } from "./pages/PublicLedger";
import { useAuth } from "./AuthContext";

// Tabs
import { SignalBoard }                            from "./SignalBoard";
import { ConvergenceBoard }                       from "./ConvergenceBoard";
import { MarketsTab }                             from "./MarketsTab";
import { RotationMapTab }                         from "./RotationMapTab";
import { OptionsTab }                             from "./OptionsTab";
import { ComplexOrdersTab, clearDeepLink, parseDeepLink } from "./ComplexOrdersTab";
import { openTapeItem }                           from "./components/LiveTape";
import { BondsTab, EconomyTab }                   from "./MacroTab";
import { FilingsTab, AltDataTab, DeepSignalsTab, DarkPoolTab, UnusualOptionsTab, CongressionalTab } from "./AltDataTab";
import { PhysicalIntelTab }                       from "./PhysicalIntelTab";
import FollowTheMoneyTab                          from "./FollowTheMoneyTab";
import { FormDPanel }                             from "./FormDPanel";
import { ReplayTab }                             from "./ReplayTab";
import { WatchlistTab, Sidebar }      from "./WatchlistTab";
import { FactorExposurePanel }                    from "./FactorExposurePanel";
import { SqueezeTab }                             from "./SqueezeTab";
import { CryptoTab, SentimentTab }               from "./CryptoSentiment";
import { ChartsTab }                              from "./ChartsTab";
import { ThesisDesk }                             from "./ThesisDesk";
import { NewsTab }                                from "./NewsTab";
import { TickerConfluenceTab }                    from "./TickerConfluenceTab";
import { InternalLogin }                          from "./pages/InternalLogin";

// ─── INTERNAL ACCESS ──────────────────────────────────────────────────────────
// Optional, gitignored module. import.meta.glob resolves to {} when the file is
// absent (public build), so this compiles with or without src/internal.js.
const _internalModule = Object.values(
  import.meta.glob("./internal.js", { eager: true })
)[0];

// ⚠⚠ UI-ONLY TRUST — THE CLIENT DECIDES WHAT TO RENDER, THE SERVER DECIDES WHAT
// TO RETURN, AND THEY ARE INDEPENDENT LAYERS. The token path below reads an
// UNVERIFIED JWT payload: no signature check, because doing one in the browser
// would need the server's secret and would prove nothing anyone could not fake
// anyway. A hand-written localStorage value with {"internal":true} gets someone
// the console shell and NOTHING ELSE — every request carries that same forged
// token to a backend that validates HS256 on every call, so the whole UI fills
// with 401s. Do not "harden" this by adding client-side verification; the
// boundary that matters is already on the other side of the wire.
const INTERNAL_FALLBACK_USER = {
  tier: "nous_topology",
  name: "NT Internal",
  // ⚠ null on the token path: the TOKEN is the credential. Nothing downstream
  // should ever reach for a passphrase here, and null makes that fail loudly
  // rather than silently comparing against undefined.
  NOUS_INTERNAL_PASSPHRASE: null,
};

/** The internal user from the internal token, or null. Never throws.
 *
 *  ⚠ The DECODE lives in core.jsx and is imported, not repeated — core is the
 *  bottom of the dependency graph and backendFetch needs the same check to pick
 *  which credential to send. This function owns the POLICY: what to do with an
 *  expired token, which is the part the callers differ on.
 */
function internalUserFromToken() {
  try {
    const payload = internalTokenPayload();
    if (!payload) return null;
    // ⚠⚠ EXPIRY IS CHECKED AND ACTED ON, NOT JUST REPORTED. The token lasts 12h
    // and nothing refreshes it on the production path (the refresh effect below
    // needs _internalModule, which the public build does not have). Left in
    // place, an expired token would keep ntUser truthy, sail past the AuthScreen
    // gate, and hand the user a fully rendered console where every single panel
    // 401s. Clearing it here turns that into one clean bounce to the login.
    if (typeof payload.exp !== "number" || payload.exp <= Date.now() / 1000) {
      // ⚠ ONLY the internal key. qs_tier and qs_auth_token belong to the
      // Supabase flow, and an expired internal token must not sign out a real
      // user who happens to be logged in beside it.
      try {
        localStorage.removeItem(INTERNAL_TOKEN_KEY);
      } catch { /* not fatal */ }
      return null;
    }
    return INTERNAL_FALLBACK_USER;
  } catch {
    // A malformed or non-JWT token is simply "not internal".
    return null;
  }
}

function getInternalUser() {
  // 1. A live token wins, wherever we are running.
  //    ⚠⚠ THE MODULE USED TO WIN UNCONDITIONALLY, WHICH MADE /internal USELESS ON
  //    LOCALHOST. With internal.js present, getInternalUser() took the module
  //    branch and never looked at qs_auth_token — so logging in through the new
  //    page stored a token that this function was structurally incapable of
  //    reading, and the dev machine bounced to AuthScreen exactly like
  //    production did. Checking the token first makes the login page work in
  //    both places.
  const viaToken = internalUserFromToken();
  if (viaToken) return viaToken;

  // 2. Local dev fallback: the gitignored module, matched against localStorage.
  //    ⚠ Reached only when there is no live token, so a dev machine that never
  //    visits /internal behaves exactly as it always has.
  if (_internalModule) {
    try {
      return localStorage.getItem("nt_internal_access") === _internalModule.NOUS_INTERNAL_PASSPHRASE
        ? _internalModule.INTERNAL_USER
        : null;
    } catch {
      return null;
    }
  }
  return null;
}

// ─── TAB MANIFEST ─────────────────────────────────────────────────────────────
// Grouped sidebar nav. Each group is { label, items }; a null label is an
// unlabeled group (top-level headline tabs / bottom utility). Desk labels render
// as non-clickable section headers. Item strings must match the activeTab routes.
// REPLAY is appended to OPTIONS DESK at runtime, and only once the backend
// reports 30 days of archived history (see buildNav / the replay_ready fetch).
// Until then the entry simply does not exist — no placeholder, no teaser.
const BASE_NAV = [
  // ⚠ ALPHA LEDGER used to lead this group. It was removed with the table behind
  // it — the ledger scored signals out of alpha_ledger, which held 0 rows in
  // production for its entire life. CONVERGENCE was already the default tab, so
  // nothing here changes where a session lands; the per-thesis record on the
  // THESIS DESK is the surviving performance view.
  { label: null, items: ["CONVERGENCE", "THESIS DESK", "SENTIMENT", "ECONOMY", "BONDS", "NEWS"] },
  // ⚠ TICKER CONFLUENCE sits with the US equity tabs, ahead of CRYPTO, deliberately
  // — it is a cross-asset read on US equities, so it belongs beside them rather
  // than at the end of the desk.
  // ⚠ DARK POOL moved here from OPTIONS DESK. Tabs are routed by name alone
  // (activeTab), never by desk, so every jump to "DARK POOL" (the Convergence
  // Board hand-off, the REPLAY fallback) still lands on it unchanged.
  { label: "MARKETS DESK", items: ["CHARTS", "MARKETS", "ROTATION MAP", "DARK POOL", "TICKER CONFLUENCE", "CRYPTO", "SQUEEZE"] },
  // ⚠⚠ THE LABEL STRING "OPTIONS DESK" IS LOAD-BEARING — buildNav below matches on
  // it exactly to append REPLAY. Renaming this desk silently drops the Replay tab
  // with no error anywhere.
  { label: "OPTIONS DESK",  items: ["UNUSUAL OPTIONS", "COMPLEX ORDERS", "OPTIONS"] },
  { label: "ALT DATA DESK", items: ["ALT DATA", "PHYSICAL INTEL", "DEEP SIGNALS", "FILINGS"] },
  { label: "MONEY MAP DESK", items: ["FOLLOW THE MONEY", "CONGRESSIONAL", "PRIVATE MARKETS"] },
  { label: null, items: ["WATCHLIST"] },
];

function buildNav(replayReady) {
  if (!replayReady) return BASE_NAV;
  return BASE_NAV.map((group) =>
    group.label === "OPTIONS DESK"
      ? { ...group, items: [...group.items, "REPLAY"] }
      : group
  );
}

// ─── SIGNAL BOARD PREFETCH ────────────────────────────────────────────────────
// On app load, warm the Signal Board by fetching the signal-relevant data
// sources in the background and pushing each result onto the data bus with the
// SAME key + shape its owning tab uses (verified against each tab's onData
// call) — so the rules engine fires immediately without the user visiting every
// tab. Fire-and-forget: Promise.allSettled means one failing source never
// blocks the others, and nothing is surfaced to the UI. Additive only — the
// tabs still fetch on navigation; the backend Redis cache dedupes.
//
// NOT prefetched here: yieldSpread + monetaryRegime (their shapes are computed
// by helpers internal to MacroTab that this module cannot reach without
// modifying that file); pcRatio + unusualOptions (require a full options-chain
// fetch). Those still populate when the Bonds/Economy/Options tabs are opened.
async function prefetchSignalData(onData, keys = {}) {
  if (!onData) return;
  const push = (key, val) => { try { onData(key, val); } catch { /* ignore */ } };
  const fredKey = keys.fredKey || "";
  const eiaKey  = keys.eiaKey  || "DEMO_KEY";

  // VIX + SKEW from FRED — mirrors OptionsTab's fetch + transform exactly.
  const vixSkew = async () => {
    const [vr, sr, hvr] = await Promise.all([
      backendFetch(`${FRED_BASE}?series_id=VIXCLS&api_key=${fredKey}&file_type=json&limit=30&sort_order=desc`,  `/api/fred/VIXCLS?limit=30`),
      backendFetch(`${FRED_BASE}?series_id=SKEW&api_key=${fredKey}&file_type=json&limit=1&sort_order=desc`,      `/api/fred/SKEW?limit=1`),
      backendFetch(`${FRED_BASE}?series_id=VIXCLS&api_key=${fredKey}&file_type=json&limit=252&sort_order=desc`, `/api/fred/VIXCLS?limit=252`),
    ]);
    const [vd, sd, hvd] = await Promise.all([vr.json(), sr.json(), hvr.json()]);
    if (vd.observations) {
      const obs  = vd.observations.filter((o) => o.value !== ".").slice(0, 20).reverse();
      const cur  = parseFloat(obs[obs.length - 1]?.value);
      const allV = (hvd.observations || []).filter((o) => o.value !== ".").map((o) => parseFloat(o.value));
      push("vix", {
        current: cur,
        history: obs.map((o) => ({ date: o.date.slice(5), value: parseFloat(o.value) })),
        min52:   Math.min(...allV),
        max52:   Math.max(...allV),
      });
    }
    if (sd.observations?.[0]) push("skew", parseFloat(sd.observations[0].value));
  };

  // Petroleum from EIA — mirrors AltDataTab (millions of bbl, chronological).
  const petroleum = async () => {
    const res = await backendFetch(
      `${EIA_BASE}/petroleum/stoc/wstk/data/` +
      `?frequency=weekly&data[0]=value` +
      `&facets[product][]=EPC0&facets[duoarea][]=NUS` +
      `&sort[0][column]=period&sort[0][direction]=desc` +
      `&length=20&api_key=${eiaKey}`,
      `/api/eia/petroleum`
    );
    const data = await res.json();
    if (data.observations?.length) {
      push("petroleum", data.observations
        .map((o) => ({ date: o.date, value: parseFloat(o.value) / 1000 }))
        .reverse());
    }
  };

  // News sentiment — mirrors NewsTab (markets feed → newsService scoring).
  const news = async () => {
    const res = await backendFetch("https://newsdata.io", "/api/news?category=markets");
    if (!res.ok) return;
    const data = await res.json();
    const arts = Array.isArray(data) ? data : [];
    if (arts.length) push("newsSentiment", newsService.computeOverallSentiment(arts));
  };

  // Alt-data lists pushed through as-is — same key + shape as AltDataTab /
  // DeepSignalsTab (backend returns an array or { data: [...] }).
  // ⚠ Placeholder payloads are dropped, not pushed: the signal rules have no
  // way to tell stand-in rows from real ones once they are on the bus.
  const altList = async (directUrl, path, key) => {
    const res = await backendFetch(directUrl, path);
    if (!res.ok) throw new Error(String(res.status));
    const list = liveRows(await res.json());
    if (list.length) push(key, list);
  };

  // Convergence Board summary endpoints — Redis-only backend readers that
  // self-cache. Prefetching them warms that cache so ConvergenceBoard's own
  // mount fetch returns instantly from Redis instead of recomputing / hitting
  // upstream. The object is also pushed to the bus (additive; the board reads
  // its own local state, so this is only for any future bus consumer).
  const summary = async (path, key) => {
    const res = await backendFetch(path, path);
    if (!res.ok) throw new Error(String(res.status));
    const data = await res.json();
    if (data && typeof data === "object") push(key, data);
  };

  await Promise.allSettled([
    vixSkew(),
    petroleum(),
    news(),
    altList("https://firms.modaps.eosdis.nasa.gov/api/area/csv/VIIRS_SNPP_NRT/world/1", "/api/altdata/thermal",  "fires"),
    altList("https://www.vesselfinder.com",  "/api/altdata/tankers",  "tankers"),
    altList("https://api.quiverquant.com",   "/api/altdata/congress", "congressTrades"),
    altList("https://api.usaspending.gov/api/v2/search/spending_by_award/", "/api/contracts", "contracts"),
    summary("/api/digest/market",     "digestMarket"),
    summary("/api/dashboard/rates",   "dashboardRates"),
    summary("/api/dashboard/physical","dashboardPhysical"),
    summary("/api/dashboard/altdata", "dashboardAltdata"),
  ]);
}

// ─── TAB ERROR BOUNDARY ───────────────────────────────────────────────────────
// One failing tab should never crash the whole terminal.
class TabBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) return (
      <div style={{ padding: 20, color: "#FF5252", fontFamily:
        "'JetBrains Mono', monospace", fontSize: 14 }}>
        <div style={{ marginBottom: 8 }}>TAB ERROR</div>
        <div style={{ color: "#4a5a6a" }}>{this.state.error.message}</div>
        <button onClick={() => this.setState({ error: null })}
          style={{ marginTop: 10, background: "#1E2330", border:
            "1px solid #1e2d40", color: "#E2E8F0", padding: "4px 10px",
            borderRadius: 2, cursor: "pointer", fontFamily: "inherit" }}>
          RETRY
        </button>
      </div>
    );
    return this.props.children;
  }
}

// ─── ROOT APP ─────────────────────────────────────────────────────────────────

// ⚠⚠ THE PLATFORM HAS NO ROUTER. Everything below /ledger is tab state
// (activeTab), which is fine for an authenticated single-page console but
// cannot express a public URL. Rather than pull react-router in for one route,
// the path is matched once here and the public page is returned instead of the
// console.
//
// ⚠ The '@' is OPTIONAL in the pattern and dropped from the capture, so
// /ledger/foo and /ledger/@foo are the same ledger. The service lower-cases and
// strips it again server-side; doing it here as well means the fetch URL is
// already canonical.
//
// ⚠ public/_redirects already carries `/* /index.html 200`, so Cloudflare serves
// the SPA for this path rather than 404ing before React ever loads.
const LEDGER_PATH = /^\/ledger\/@?([A-Za-z0-9_]{3,30})\/?$/;

// ⚠⚠ A SEPARATE COMPONENT, NOT AN EARLY RETURN INSIDE AuthedApp. AuthedApp
// declares ~20 hooks and fires session checks, /api/me and a replay probe on
// mount; branching after those would run every one of them — and 401 on each —
// for a visitor who has no account and is only reading someone's public page.
// Returning before AuthedApp mounts means none of it happens.
// ⚠ Trailing slash tolerated — /internal and /internal/ are the same page, and
// a stray slash silently dropping someone into AuthScreen is a confusing way to
// fail on a URL nothing links to and everyone types by hand.
const INTERNAL_PATH = /^\/internal\/?$/;

export default function App() {
  const m = LEDGER_PATH.exec(window.location.pathname);
  if (m) return <PublicLedger handle={m[1]} />;
  // ⚠⚠ Same pattern as PublicLedger — branch BEFORE AuthedApp mounts so a page
  // that needs neither a session nor a profile does not fire the session check
  // and /api/me and 401 on both. It is also the auth page itself, so it must sit
  // outside every auth gate: there is no session yet, by definition.
  if (INTERNAL_PATH.test(window.location.pathname)) return <InternalLogin />;
  return <AuthedApp />;
}

function AuthedApp() {
  const [keys]                            = useState(() => loadKeys() || {});
  const [complexLink,   setComplexLink]   = useState(() => parseDeepLink(window.location.search));   // a shared package link opens that package's chart; so does a tape click
  const [uoFocus,       setUoFocus]       = useState(null);   // {symbol, nonce} — a tape click into Unusual Options for that ticker
  const [activeTab,     setActiveTab]     = useState(complexLink ? "COMPLEX ORDERS" : "CONVERGENCE");
  const [dpFocus,       setDpFocus]       = useState(null);   // {symbol, nonce} — deep-link into Dark Pool detail
  const [dpResults,     setDpResults]     = useState(null);   // dark-pool scan results handed off from the Convergence Board
  const [ftmFocus,      setFtmFocus]      = useState(null);   // {name, nonce} — deep-link into Follow the Money with a member pre-loaded
  const [congressFocus, setCongressFocus] = useState(null);   // {symbol, nonce} — a Ticker Confluence badge into that ticker's reported trades
  const [replayReady,   setReplayReady]   = useState(false);  // gates the REPLAY tab (30 days of archived history)
  // ⚠ null = not yet known. Three states, not two: "no thesis" and "have not
  // asked yet" must not both read as false, or the landing tab would flip to
  // CONVERGENCE for a moment on every load for a user who HAS a thesis.
  const [hasThesis,     setHasThesis]     = useState(null);
  const [replayDaysLeft, setReplayDaysLeft] = useState(null); // days_until_ready from /api/replay/status
  const [showSettings,  setShowSettings]  = useState(false);  // 👤 account — SettingsModal (config.jsx)
  const [showAppSettings, setShowAppSettings] = useState(false); // ⚙ platform — SettingsPanel.jsx
  const [clock,         setClock]         = useState(new Date().toLocaleTimeString());
  const [globalSignals, setGlobalSignals] = useState({});
  const [acknowledged,  setAcknowledged]  = useState(false);
  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [isMobile,      setIsMobile]      = useState(
    () => typeof window !== "undefined" &&
      window.matchMedia("(max-width: 768px)").matches
  );

  const { user, loading, tier, me } = useAuth();

  // Internal (Nous Topology) access — bypasses auth + ack + plan gating.
  const ntUser = getInternalUser();

  // ── Clock ──
  useEffect(() => {
    const t = setInterval(() => setClock(new Date().toLocaleTimeString()), 1000);
    return () => clearInterval(t);
  }, []);

  // ── Internal access exchanges the passphrase for a JWT ──
  // ⚠⚠ THE PASSPHRASE IS NO LONGER A BEARER TOKEN. It used to be written
  // straight into qs_auth_token and sent as the Authorization header on every
  // request, because the backend accepted it as one — which made a single
  // static env var simultaneously an admin credential and a master login on
  // every authenticated endpoint. The backend dropped that path on 2026-08-29;
  // POST /auth/internal is the supported exchange and returns a 12h JWT, and
  // that token is what backendFetch now carries.
  // ⚠ The token is refreshed on an interval as well as at mount: it expires
  // after 12h, and without the refresh a long-lived tab would start 401ing
  // silently with no obvious cause.
  useEffect(() => {
    if (!ntUser || !_internalModule) return;
    let cancelled = false;

    const exchange = async () => {
      const backend = window.QUANT_SIGNAL_BACKEND;
      if (!backend) return;
      try {
        const res = await fetch(`${backend}/auth/internal`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            passphrase: _internalModule.NOUS_INTERNAL_PASSPHRASE,
          }),
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled || !data || !data.access_token) return;
        // ⚠⚠ INTERNAL_TOKEN_KEY, not qs_auth_token. This refresh is the LAST
        // place that still wrote the internal JWT into the Supabase slot: on a
        // dev machine with internal.js it would stomp a signed-in user's session
        // every 11 hours, and — now that the internal session reads its own key
        // — it would also have written somewhere nothing reads, silently
        // expiring the dev session at 12h with no refresh.
        localStorage.setItem(INTERNAL_TOKEN_KEY, data.access_token);
        localStorage.setItem("qs_tier", data.tier || "nous_topology");
      } catch {
        // Internal mode is a local convenience — never break the app over it.
      }
    };

    exchange();
    const t = setInterval(exchange, 11 * 60 * 60 * 1000); // refresh before 12h
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [ntUser]);

  // ── Per-session legal acknowledgement gate ──
  useEffect(() => {
    if (isAcknowledged(!!complexLink)) setAcknowledged(true);
  }, []);

  const handleAffirm = useCallback(() => {
    recordAcknowledged();
    setAcknowledged(true);
  }, []);

  const handleDecline = useCallback(() => {
    clearAcknowledged();
    window.location.href = "https://quant-signal.ai";
  }, []);

  const closeComplexLink = useCallback(() => {
    clearDeepLink();
    setComplexLink(null);
  }, []);

  // Stable, because Ticker Confluence memoises its rows on them.
  const openCongressTicker = useCallback((symbol) => {
    setCongressFocus({ symbol, nonce: Date.now() });
    setActiveTab("CONGRESSIONAL");
  }, []);
  const congressFocusUsed = useCallback(() => setCongressFocus(null), []);
  const backToConfluence = useCallback(() => setActiveTab("TICKER CONFLUENCE"), []);

  // ── Sync backend URL to window so backendFetch() routes through it ──
  useEffect(() => {
    if (keys?.backendUrl) {
      window.QUANT_SIGNAL_BACKEND = keys.backendUrl;
    }
  }, [keys?.backendUrl]);

  // ── Data bus — tabs push live data here, SignalBoard reads it ──
  const handleData = useCallback((key, value) => {
    setGlobalSignals((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ── Watchlist symbols for the hidden FactorExposurePanel (see the CONVERGENCE
  //    branch below) ──
  // ⚠ KEYED ON activeTab, NOT []. localStorage is not reactive: WatchlistTab owns
  // the list and writes it on change, so a plain mount-once read would go stale the
  // moment a symbol is added there. Re-reading whenever the active tab changes
  // means returning to CONVERGENCE picks up the edit — which is exactly when the
  // hidden panel remounts and recomputes anyway.
  // ⚠ Falls back to [] on absent/corrupt storage; FactorExposurePanel early-returns
  // below 2 symbols, so an empty or single-name list simply publishes nothing.
  const watchlistSymbols = useMemo(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("fterm_watchlist"));
      if (!Array.isArray(raw)) return [];
      return raw
        .map((x) => (typeof x === "string" ? x : x?.symbol))
        .filter(Boolean)
        .map((sym) => String(sym).toUpperCase());
    } catch {
      return [];
    }
  }, [activeTab]);

  // ── Auto-populate the Signal Board on load ──
  // Fire the background prefetch once, after auth is established (so it uses the
  // logged-in token and never 401s pre-login) and 1s after mount so the initial
  // render paints first. Purely background — no loading/error state shown.
  const prefetchedRef = useRef(false);
  useEffect(() => {
    if (prefetchedRef.current || !(user || ntUser)) return;
    prefetchedRef.current = true;
    const t = setTimeout(() => { prefetchSignalData(handleData, keys); }, 1000);
    return () => clearTimeout(t);
  }, [user, ntUser, keys, handleData]);

  // ── Replay feature flag ──
  // The Replay tab stays hidden until the backend has archived 30 days of
  // stream history. Fetched once after auth; any failure leaves it hidden.
  useEffect(() => {
    if (!(user || ntUser)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await backendFetch("/api/replay/status", "/api/replay/status");
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data || typeof data !== "object") return;
        setReplayReady(data.replay_ready === true);
        setReplayDaysLeft(
          typeof data.days_until_ready === "number" ? data.days_until_ready : null
        );
      } catch { /* hidden by default — never surfaced */ }
    })();
    return () => { cancelled = true; };
  }, [user, ntUser]);

  const NAV = useMemo(() => buildNav(replayReady), [replayReady]);

  // If the flag flips off while REPLAY is open, fall back to a always-present tab.
  useEffect(() => {
    if (!replayReady && activeTab === "REPLAY") setActiveTab("DARK POOL");
  }, [replayReady, activeTab]);

  // ── Thesis presence ──
  // ⚠ One call on mount. THESIS DESK is always in the nav — it shows the
  // builder when there is no thesis — so this decides only where the user
  // LANDS, never whether the tab exists.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await backendFetch("/api/thesis/me", "/api/thesis/me");
        if (!cancelled) setHasThesis(r.ok);
      } catch {
        // ⚠ Unreachable is treated as NO thesis for routing only. Landing on
        // the desk would show its own error state; CONVERGENCE still works.
        if (!cancelled) setHasThesis(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ⚠⚠ FIRES ONCE, AND ONLY WHILE THE USER IS STILL ON THE DEFAULT TAB.
  // Without the activeTab check this would yank someone back to the desk if
  // the fetch resolved after they had already navigated somewhere else.
  const thesisLandedRef = useRef(false);
  useEffect(() => {
    if (hasThesis !== true || thesisLandedRef.current) return;
    thesisLandedRef.current = true;
    setActiveTab((cur) => (cur === "CONVERGENCE" ? "THESIS DESK" : cur));
  }, [hasThesis]);

  // ── Mobile detection — drives the touch drawer vs the desktop hover rail ──
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // ── Sidebar open/close (touch-first: fire on pointerdown, no click delay) ──
  const openSidebar  = useCallback(() => setSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleSidebar = useCallback((e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setSidebarOpen((prev) => !prev);
  }, []);
  // Navigate + close in one tap (mobile drawer).
  const handleMobileSelect = useCallback((t) => {
    setActiveTab(t);
    setSidebarOpen(false);
  }, []);

  // Dark-pool results handed off from the Convergence Board expire after 60s so a
  // later Dark Pool tab open shows a fresh scan, not stale convergence results.
  useEffect(() => {
    if (!dpResults) return undefined;
    const id = setTimeout(() => setDpResults(null), 60000);
    return () => clearTimeout(id);
  }, [dpResults]);

  // ── Swipe detection — right-from-edge opens, left closes ──
  const SIDEBAR_WIDTH = 200;
  const touchStartRef = useRef(null);
  const mainRef = useRef(null);   // s.main scroll container — reset to top on sidebar open
  const handleTouchStart = useCallback((e) => {
    const t = e.touches && e.touches[0];
    if (t) touchStartRef.current = { x: t.clientX, y: t.clientY };
  }, []);
  // Open: touch begins within 30px of the left edge and moves right > 50px.
  const handleEdgeTouchEnd = useCallback((e) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - start.x, dy = t.clientY - start.y;
    if (Math.abs(dx) > Math.abs(dy) && start.x < 30 && dx > 50) openSidebar();
  }, [openSidebar]);
  // Close: swipe left on the open drawer.
  const handleDrawerTouchEnd = useCallback((e) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - start.x, dy = t.clientY - start.y;
    if (Math.abs(dx) > Math.abs(dy) && dx < -50) closeSidebar();
  }, [closeSidebar]);
  // Main content: open from the left edge, or close by swiping left past the drawer.
  const handleMainTouchEnd = useCallback((e) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - start.x, dy = t.clientY - start.y;
    if (Math.abs(dx) <= Math.abs(dy)) return; // vertical scroll — ignore
    if (!sidebarOpen && start.x < 30 && dx > 50) openSidebar();
    else if (sidebarOpen && start.x > SIDEBAR_WIDTH && dx < -50) closeSidebar();
  }, [sidebarOpen, openSidebar, closeSidebar]);

  // On sidebar OPEN (any platform / any trigger), reset scroll to the top so the
  // full tab list is visible. The real scroll container is document.body (the
  // #root zoom:1.07 on desktop pushes content past 100vh, so the document — body
  // — scrolls, not s.main). Run synchronously: a setTimeout here gets cancelled by
  // the effect cleanup on the next sidebarOpen change (desktop hover flicker).
  // Fires on open only (guarded on sidebarOpen); does nothing on close.
  useEffect(() => {
    if (!sidebarOpen) return;
    document.body.scrollTo({ top: 0 });
  }, [sidebarOpen]);

  // ── Trial badge state (from the backend profile) ──
  const trialEndDate = me?.trial_end ? new Date(me.trial_end) : null;
  const trialDays = trialEndDate
    ? Math.max(0, Math.ceil((trialEndDate.getTime() - Date.now()) / 86400000))
    : null;
  const onTrial = !ntUser && trialDays != null &&
    (tier === "trial" || me?.subscription_status === "trialing");

  // ── Render gates (priority order) ──

  // 1. Checking for an existing session.
  if (loading && !ntUser) {
    return (
      <div style={{
        minHeight: "100vh", background: C.bg, fontFamily: font,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: C.amber, fontWeight: 700, letterSpacing: "0.2em", fontSize: 16,
      }}>
        ◈ INITIALIZING...
      </div>
    );
  }

  // 2. Not authenticated (internal access bypasses auth).
  if (!user && !ntUser) {
    return <AuthScreen />;
  }

  // 3. Authenticated but not acknowledged this session.
  if (!acknowledged && !ntUser) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg }}>
        <AcknowledgementModal onAffirm={handleAffirm} onDecline={handleDecline} />
      </div>
    );
  }

  return (
    <div style={s.app}>
      {/* Account modal — email, plan, Anthropic key, billing, sign out */}
      {showSettings && (
        <SettingsModal onClose={() => setShowSettings(false)} internal={!!ntUser} />
      )}

      {/* Platform settings — squawk box + keyword alerts */}
      {showAppSettings && (
        <SettingsPanel onClose={() => setShowAppSettings(false)} />
      )}

      {/* Header — logo left · empty center · clock / settings / NT badge right */}
      <div style={s.header}>
        {/* ⚠ The glyph was 34px text inline before 17px text; the SVG is
            display:block, so the row needs to be a flex box or the mark and the
            wordmark stop sharing a baseline. s.logo's colour, size and tracking
            are untouched — only the ◈ becomes the real mark. */}
        <div style={{ ...s.logo, display: "flex", alignItems: "center", gap: 8 }}>
          <NousIcon size={32} color={C.amber} />
          QUANT-SIGNAL
        </div>

        <div style={{ flex: 1 }} />

        {/* Header right */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, marginLeft: 8 }}>
          {ntUser && (
            <span style={{ fontSize: 12, fontWeight: 700, color: C.amber, letterSpacing: "0.1em" }}>
              ◈ NT
            </span>
          )}
          {onTrial && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                color: trialDays > 3 ? "#FFD700" : "#FF5252",
                ...(trialDays <= 3 ? { animation: "qsPulse 1.5s ease-in-out infinite" } : {}),
              }}
            >
              {trialDays > 3 ? `TRIAL · ${trialDays} DAYS` : `TRIAL ENDS IN ${trialDays} DAYS`}
            </span>
          )}
          <div style={{ fontSize: 13, color: C.textDim, fontFamily: font }}>
            {clock}
          </div>
          {/* 👤 Account — opens the existing SettingsModal unchanged (email,
              plan, Anthropic key, subscription, sign out). */}
          <button
            style={{ ...s.btn(), padding: "4px 8px" }}
            onClick={() => setShowSettings(true)}
            title="Account & Login"
            aria-label="Account"
          >
            <User size={11} />
          </button>
          {/* ⚙ Platform settings — squawk box + keyword alerts. */}
          <button
            style={{ ...s.btn(), padding: "4px 8px" }}
            onClick={() => setShowAppSettings(true)}
            title="Platform Settings"
            aria-label="Settings"
          >
            <Settings size={11} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ ...s.body, display: "flex", flexDirection: "row", flex: 1, overflow: "hidden" }}>
        {isMobile ? (
          // ── Mobile: overlay drawer (tap toggle / tap edge / swipe / backdrop) ──
          <>
            {/* Left-edge tap + swipe-open zone — above content, below sidebar */}
            {!sidebarOpen && (
              <div
                onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); openSidebar(); }}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleEdgeTouchEnd}
                style={{ position: "fixed", left: 0, top: 44, bottom: 0, width: 20, zIndex: 40 }}
              />
            )}
            {/* Diamond toggle — 44×44 touch target, visual glyph unchanged */}
            {!sidebarOpen && (
              <div
                onPointerDown={toggleSidebar}
                title="Menu"
                style={{
                  // ⚠⚠ CENTRED LIKE THE DESKTOP RAIL, NOT PINNED UNDER THE HEADER.
                  // This was top: 48 — 4px below the 44px header — so the mobile
                  // handle sat top-left while the desktop one sat mid-screen.
                  // Same control, two unrelated style objects; nothing is shared
                  // between the branches, so they had drifted apart silently.
                  position: "fixed", left: 0, top: "50vh",
                  // ⚠⚠ THE TRANSFORM IS ON THE WRAPPER, THE ROTATION ON THE SPAN,
                  // AND THEY CANNOT SHARE AN ELEMENT. transform is a single
                  // property: putting translateY here and rotate on the same node
                  // would mean one declaration overwriting the other. Splitting
                  // them across the two elements also keeps the 44×44 tap target
                  // moving WITH the glyph rather than drifting half a glyph away
                  // from it, which is what a transform on the inner span alone
                  // would have done.
                  transform: "translateY(-50%)",
                  minWidth: 44, minHeight: 44, width: 44, height: 44,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", zIndex: 41,
                }}
              >
                <span style={{ color: C.amber, fontSize: 24, transform: "rotate(90deg)" }}>◈</span>
              </div>
            )}
            {/* Backdrop — tap closes; sits directly beneath the drawer */}
            {sidebarOpen && (
              <div
                onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); closeSidebar(); }}
                style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 49 }}
              />
            )}
            {/* Sliding drawer — swipe left to close */}
            <div
              onTouchStart={handleTouchStart}
              onTouchEnd={handleDrawerTouchEnd}
              style={{
                position: "fixed", top: 0, bottom: 0, left: 0, width: SIDEBAR_WIDTH,
                zIndex: 50,
                transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
                transition: "transform 200ms ease-in-out",
                boxShadow: sidebarOpen ? "2px 0 14px rgba(0,0,0,0.45)" : "none",
              }}
            >
              <Sidebar keys={keys} tabs={NAV} activeTab={activeTab} onSelect={handleMobileSelect} mobile />
            </div>
          </>
        ) : (
          // ── Desktop: 12px hover rail + sliding nav panel (unchanged) ──
          <div
            style={{ display: "flex", flexShrink: 0, height: "100%" }}
            onMouseLeave={() => setSidebarOpen(false)}
          >
            <div
              onMouseEnter={() => setSidebarOpen(true)}
              style={{
                width: 12,
                minWidth: 12,
                height: "100%",
                background: C.panel,
                borderRight: `1px solid ${C.border}`,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {!sidebarOpen && (
                <span
                  onMouseEnter={() => setSidebarOpen(true)}
                  onClick={() => setSidebarOpen((o) => !o)}
                  title="Navigation"
                  style={{
                    color: C.amber, fontSize: 24,
                    position: "fixed", top: "50vh", left: 0,
                    transform: "translateY(-50%) rotate(90deg)",
                    cursor: "pointer", zIndex: 30,
                  }}
                >◈</span>
              )}
            </div>
            <div
              style={{
                width: sidebarOpen ? 200 : 0,
                overflow: "hidden",
                transition: "width 200ms ease-in-out",
                height: "100%",
                flexShrink: 0,
              }}
            >
              <Sidebar keys={keys} tabs={NAV} activeTab={activeTab} onSelect={setActiveTab} />
            </div>
          </div>
        )}

        <div
          ref={mainRef}
          style={s.main}
          onTouchStart={isMobile ? handleTouchStart : undefined}
          onTouchEnd={isMobile ? handleMainTouchEnd : undefined}
        >
          {activeTab === "THESIS DESK" && (
            <TabBoundary key="thesis-desk">
              <ThesisDesk onNavigate={setActiveTab} />
            </TabBoundary>
          )}
          {activeTab === "CONVERGENCE" && (
            <TabBoundary key="convergence">
              <ConvergenceBoard keys={keys} globalSignals={globalSignals} onData={handleData} onNavigate={setActiveTab}
                onDpNavigate={(r) => { setDpResults(r); setActiveTab("DARK POOL"); }}
                onTapeOpen={(item) => openTapeItem(item, { setTab: setActiveTab, setDpFocus, setUoFocus, setComplexLink })} />
              {/* SignalBoard stays mounted but hidden: its detection useEffect must
                  keep publishing activeSignals/signalHistory into globalSignals,
                  which ConvergenceBoard consumes. Rendering it here (not visibly)
                  preserves existing signal detection without duplicating the UI. */}
              <div style={{ display: "none" }} aria-hidden="true">
                <SignalBoard keys={keys} globalSignals={globalSignals} onData={handleData} />
              </div>
              {/* FactorExposurePanel, same treatment and for the same reason: it is
                  the ONLY producer of portfolioExposures /
                  portfolioIndependentPositions, and it lived exclusively inside the
                  Watchlist tab. Until a user visited that tab the Convergence Board
                  had no exposures at all, which left four separate surfaces dark —
                  the Section 1 read, the relevance line on all six domain cards, the
                  per-signal relevance badges, and the Watchlist Context card's beta
                  and independent-position rows.
                  ⚠⚠ MOUNTED INSIDE THIS BRANCH, NOT AT THE TOP OF THE TREE. The
                  Watchlist tab still renders its own visible instance; a top-level
                  mount would run BOTH at once whenever that tab is open, doubling
                  the N+1 daily-bar fan-out and racing two writers onto the same
                  globalSignals key. The two tabs are mutually exclusive, so scoping
                  it here makes that impossible.
                  ⚠ Cost note: CONVERGENCE is the default landing tab, so this fires
                  one /api/bars request per watchlist symbol plus SPY (11 for the
                  default 10-name list) and one EDGAR bulk POST per mount. /api/bars
                  carries a 60s server-side TTL, so remounts inside a minute are
                  cheap; beyond that they re-fetch. */}
              <div style={{ display: "none" }} aria-hidden="true">
                <FactorExposurePanel
                  symbols={watchlistSymbols}
                  globalSignals={globalSignals}
                  onData={handleData}
                />
              </div>
            </TabBoundary>
          )}
          {activeTab === "CHARTS" && (
            <TabBoundary key="charts">
              <ChartsTab keys={keys} globalSignals={globalSignals} />
            </TabBoundary>
          )}
          {/* ⚠ Distinct from the CONVERGENCE tab above: that renders the
              ConvergenceBoard (the landing page). This is the fired-event tape,
              which used to be a sub-panel inside ALT DATA and is now addressable
              on its own. It owns its fetch; its one prop is the Congress badge's way out. */}
          {activeTab === "TICKER CONFLUENCE" && (
            <TabBoundary key="ticker-confluence">
              <TickerConfluenceTab onCongressOpen={openCongressTicker} />
            </TabBoundary>
          )}
          {activeTab === "MARKETS" && (
            <TabBoundary key="markets">
              <MarketsTab keys={keys} onData={handleData} />
            </TabBoundary>
          )}
          {activeTab === "ROTATION MAP" && (
            <TabBoundary key="rotation-map">
              <RotationMapTab />
            </TabBoundary>
          )}
          {activeTab === "OPTIONS" && (
            <TabBoundary key="options">
              <OptionsTab keys={keys} onData={handleData} />
            </TabBoundary>
          )}
          {activeTab === "DARK POOL" && (
            <TabBoundary key="dark-pool">
              <DarkPoolTab keys={keys} onData={handleData} focusSymbol={dpFocus} globalSignals={globalSignals}
                dpResults={dpResults} onDpResultsUsed={() => setDpResults(null)}
                onNavigate={setActiveTab} />
            </TabBoundary>
          )}
          {activeTab === "BONDS" && (
            <TabBoundary key="bonds">
              <BondsTab keys={keys} onData={handleData} />
            </TabBoundary>
          )}
          {activeTab === "ECONOMY" && (
            <TabBoundary key="economy">
              <EconomyTab keys={keys} onData={handleData} />
            </TabBoundary>
          )}
          {activeTab === "FILINGS" && (
            <TabBoundary key="filings">
              <FilingsTab keys={keys} />
            </TabBoundary>
          )}
          {activeTab === "ALT DATA" && (
            <TabBoundary key="alt-data">
              <AltDataTab keys={keys} onData={handleData} />
            </TabBoundary>
          )}
          {activeTab === "PHYSICAL INTEL" && (
            <TabBoundary key="physical-intel">
              <PhysicalIntelTab keys={keys} onData={handleData} globalSignals={globalSignals} />
            </TabBoundary>
          )}
          {activeTab === "FOLLOW THE MONEY" && (
            <TabBoundary key="follow-the-money">
              <FollowTheMoneyTab keys={keys} globalSignals={globalSignals} onNavigate={setActiveTab} ftmFocus={ftmFocus} />
            </TabBoundary>
          )}
          {activeTab === "PRIVATE MARKETS" && (
            <TabBoundary key="form-d">
              <FormDPanel keys={keys} />
            </TabBoundary>
          )}
          {activeTab === "DEEP SIGNALS" && (
            <TabBoundary key="deep-signals">
              <DeepSignalsTab keys={keys} onData={handleData} />
            </TabBoundary>
          )}
          {activeTab === "SQUEEZE" && (
            <TabBoundary key="squeeze">
              <SqueezeTab
                keys={keys}
                onData={handleData}
                onDarkPool={(sym) => { setDpFocus({ symbol: sym, nonce: clock }); setActiveTab("DARK POOL"); }}
              />
            </TabBoundary>
          )}
          {activeTab === "CRYPTO" && (
            <TabBoundary key="crypto">
              <CryptoTab keys={keys} onData={handleData} />
            </TabBoundary>
          )}
          {activeTab === "SENTIMENT" && (
            <TabBoundary key="sentiment">
              <SentimentTab keys={keys} onData={handleData} globalSignals={globalSignals} />
            </TabBoundary>
          )}
          {activeTab === "NEWS" && (
            <TabBoundary key="news">
              <NewsTab keys={keys} onData={handleData} globalSignals={globalSignals} />
            </TabBoundary>
          )}
          {activeTab === "WATCHLIST" && (
            <TabBoundary key="watchlist">
              <WatchlistTab keys={keys} globalSignals={globalSignals} onData={handleData} />
            </TabBoundary>
          )}
          {activeTab === "UNUSUAL OPTIONS" && (
            <TabBoundary key="unusual-options">
              <UnusualOptionsTab keys={keys} onData={handleData} onNavigate={setActiveTab}
                focusSymbol={uoFocus} onFocusUsed={() => setUoFocus(null)} />
            </TabBoundary>
          )}
          {activeTab === "COMPLEX ORDERS" && (
            <TabBoundary key="complex-orders">
              <ComplexOrdersTab link={complexLink} onCloseLink={closeComplexLink} />
            </TabBoundary>
          )}
          {activeTab === "REPLAY" && replayReady && (
            <TabBoundary key="replay">
              <ReplayTab keys={keys} />
            </TabBoundary>
          )}
          {activeTab === "CONGRESSIONAL" && (
            <TabBoundary key="congressional">
              <CongressionalTab
                keys={keys}
                onData={handleData}
                onFollowMoney={(name) => { setFtmFocus({ name, nonce: Date.now() }); setActiveTab("FOLLOW THE MONEY"); }}
                focusSymbol={congressFocus}
                onFocusUsed={congressFocusUsed}
                onBack={backToConfluence}
              />
            </TabBoundary>
          )}
        </div>
      </div>
    </div>
  );
}
